import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

type TenantUser = components['schemas']['TenantUserRead']
type Role = components['schemas']['RoleRead']
type Status = TenantUser['status']

const DURUM: Record<Status, { label: string; tone: 'info' | 'success' | 'warning' }> = {
  invited: { label: 'Davet edildi', tone: 'info' },
  active: { label: 'Aktif', tone: 'success' },
  suspended: { label: 'Erişimi kapalı', tone: 'warning' },
}

/** Ayarlar › Panel kullanıcıları — YES-84, YES-115. */
export function Kullanicilar() {
  const slug = useTenantSlug()
  const [davetAcik, setDavetAcik] = useState(false)
  const [duzenlenen, setDuzenlenen] = useState<TenantUser | null>(null)

  const kullanicilar = useQuery({
    queryKey: ['tenant-users', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/users')
      return data ?? []
    },
  })

  const roller = useQuery({
    queryKey: ['roles', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles')
      return data ?? []
    },
  })

  if (kullanicilar.isError) {
    const yetkisiz =
      kullanicilar.error instanceof ApiError && kullanicilar.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz ? 'Panel kullanıcılarını görme yetkiniz yok.' : 'Kullanıcılar yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  const rolAdi = (code: string) => roller.data?.find((rol) => rol.code === code)?.name ?? code

  return (
    <Sayfa>
      <Card>
        <CardHeader
          title="Panel kullanıcıları"
          description="Derneği yönetenler. Üyelerle karıştırılmamalı: üyeler ayrı bir listede."
          action={<Button onClick={() => setDavetAcik(true)}>Kullanıcı davet et</Button>}
        />
        <CardBody>
          {kullanicilar.data && kullanicilar.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {kullanicilar.data.map((kisi) => (
                <li key={kisi.user_id}>
                  <button
                    type="button"
                    onClick={() => setDuzenlenen(kisi)}
                    className="flex w-full flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-[var(--ad-border)] px-3 py-2.5 text-left hover:bg-[var(--ad-surface-muted)]"
                  >
                    <span className="flex min-w-48 flex-col">
                      <span className="text-sm font-medium">{kisi.full_name}</span>
                      <span className="text-xs text-[var(--ad-text-muted)]">{kisi.email}</span>
                    </span>

                    <span className="flex flex-wrap gap-1">
                      {kisi.role_codes.length > 0 ? (
                        kisi.role_codes.map((code) => (
                          <Badge key={code} tone="neutral">
                            {rolAdi(code)}
                          </Badge>
                        ))
                      ) : (
                        // Rolsüz kullanıcı derneğe girer ama hiçbir şey
                        // yapamaz; bu sessiz kalmamalı.
                        <Badge tone="warning">Rol atanmamış</Badge>
                      )}
                    </span>

                    <Badge tone={DURUM[kisi.status].tone} className="ml-auto">
                      {DURUM[kisi.status].label}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<Icon name="users" size={24} aria-hidden />}
              title="Başka yönetici yok"
              description="Yönetim kurulu üyelerini davet ederek panele alabilirsiniz."
            />
          )}
        </CardBody>
      </Card>

      <Davet acik={davetAcik} onAcik={setDavetAcik} slug={slug} />
      {duzenlenen && (
        <KullaniciDuzenle
          kisi={duzenlenen}
          roller={roller.data ?? []}
          slug={slug}
          onKapat={() => setDuzenlenen(null)}
        />
      )}
    </Sayfa>
  )
}

function Davet({
  acik,
  onAcik,
  slug,
}: {
  acik: boolean
  onAcik: (acik: boolean) => void
  slug: string | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const gonder = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/users/invite', {
        body: {
          email: String(form.get('email')),
          full_name: String(form.get('full_name')),
        },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant-users', slug] })
      onAcik(false)
      setHatalar({})
      toast({ title: 'Davet gönderildi', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({
        title: 'Davet gönderilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  return (
    <Dialog
      open={acik}
      onOpenChange={onAcik}
      title="Kullanıcı davet et"
      description="Davet e-postayla gider. Rolleri, davet kabul edildikten sonra atarsınız."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          gonder.mutate(new FormData(event.currentTarget))
        }}
      >
        <Field label="Ad soyad" required error={hatalar.full_name}>
          {(props) => <Input name="full_name" autoFocus {...props} />}
        </Field>
        <Field label="E-posta" required error={hatalar.email}>
          {(props) => <Input name="email" type="email" {...props} />}
        </Field>

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => onAcik(false)}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={gonder.isPending}>
            {gonder.isPending ? 'Gönderiliyor…' : 'Davet gönder'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function KullaniciDuzenle({
  kisi,
  roller,
  slug,
  onKapat,
}: {
  kisi: TenantUser
  roller: Role[]
  slug: string | null
  onKapat: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [secili, setSecili] = useState<Set<string>>(new Set(kisi.role_codes))

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-users', slug] })
    // Kendi rolümüzü değiştirdiysek menü ve izinler de değişir.
    void queryClient.invalidateQueries({ queryKey: ['me', 'permissions', slug] })
    void queryClient.invalidateQueries({ queryKey: ['navigation', slug] })
  }

  const rolKaydet = useMutation({
    mutationFn: async () => {
      await api.PUT('/api/v1/users/{user_id}/roles', {
        params: { path: { user_id: kisi.user_id } },
        body: { role_codes: [...secili] },
      })
    },
    onSuccess: () => {
      tazele()
      onKapat()
      toast({ title: 'Roller güncellendi', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Roller güncellenemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const durumDegis = useMutation({
    mutationFn: async (hedef: Status) => {
      await api.POST('/api/v1/users/{user_id}/status', {
        params: { path: { user_id: kisi.user_id } },
        body: { status: hedef },
      })
    },
    onSuccess: () => {
      tazele()
      onKapat()
      toast({ title: 'Erişim durumu güncellendi', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Durum değiştirilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const degistir = (code: string, acik: boolean) =>
    setSecili((onceki) => {
      const sonraki = new Set(onceki)
      if (acik) sonraki.add(code)
      else sonraki.delete(code)
      return sonraki
    })

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={kisi.full_name}
      description={kisi.email}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[var(--ad-text-muted)]">Durum</span>
          <Badge tone={DURUM[kisi.status].tone}>{DURUM[kisi.status].label}</Badge>
          {kisi.status === 'invited' && (
            <span className="text-xs text-[var(--ad-text-muted)]">
              Davet kabul edilmedi; roller şimdiden atanabilir.
            </span>
          )}
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="pb-1 text-sm font-medium">Roller</legend>
          {roller.map((rol) => (
            <label
              key={rol.code}
              className="flex items-start gap-2 rounded-[var(--radius-control)] px-2 py-1.5 hover:bg-[var(--ad-surface-muted)]"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={secili.has(rol.code)}
                onChange={(event) => degistir(rol.code, event.target.checked)}
              />
              <span className="flex flex-col">
                <span className="text-sm">{rol.name}</span>
                <span className="text-xs text-[var(--ad-text-muted)]">
                  {rol.description ?? `${rol.permissions.length} izin`}
                </span>
              </span>
            </label>
          ))}
          {secili.size === 0 && (
            <p className="px-2 text-xs text-[var(--ad-warning)]">
              Rolü olmayan kullanıcı panele girer ama hiçbir işlem yapamaz.
            </p>
          )}
        </fieldset>

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--ad-border)] pt-3">
          {/* Erişimi kapatmak SILMEK DEGIL: kayıt duruyor, böylece kimin ne
              yaptığı denetim kaydında izlenebilir kalıyor. */}
          {kisi.status === 'suspended' ? (
            <Button
              type="button"
              variant="secondary"
              disabled={durumDegis.isPending}
              onClick={() => durumDegis.mutate('active')}
            >
              Erişimi aç
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              disabled={durumDegis.isPending}
              onClick={() => durumDegis.mutate('suspended')}
            >
              Erişimi kapat
            </Button>
          )}

          <div className="ml-auto flex gap-2">
            <Button type="button" variant="secondary" onClick={onKapat}>
              Vazgeç
            </Button>
            <Button disabled={rolKaydet.isPending} onClick={() => rolKaydet.mutate()}>
              {rolKaydet.isPending ? 'Kaydediliyor…' : 'Rolleri kaydet'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />
      {children}
    </div>
  )
}
